import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getCurrentOrganization,
  organizationStatusLabel,
} from '../../services/organizationService';
import type { OrganizationDetails } from '../../types/organization';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function OrganizationDetailsScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const [org, setOrg] = useState<OrganizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await getCurrentOrganization();
      setOrg(data);
      setError(null);
    } catch (caught) {
      setOrg(null);
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.ORGANIZATION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !org ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Organization details"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading organization" />
        </AppScreen>
      ) : error && !org ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Organization details"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : org ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title="Organization details"
            onBack={navigation.goBack}
          />

          <View style={styles.body}>

          <Text style={themed.section}>Basic information</Text>
          <AppCard style={styles.card}>
            <Field palette={palette} label="Organization code" value={org.organizationCode} />
            <Field palette={palette} label="Organization name" value={org.organizationName} />
            <Field palette={palette} label="Legal name" value={org.legalName} />
            <Field palette={palette} label="Status" value={organizationStatusLabel(org)} />
            <Field
              palette={palette}
              label="Registration number"
              value={org.registrationNumber}
            />
            <Field
              palette={palette}
              label="Tax registration number"
              value={org.taxRegistrationNumber}
            />
          </AppCard>

          <Text style={themed.section}>Contact</Text>
          <AppCard style={styles.card}>
            <Field palette={palette} label="Official email" value={org.officialEmail} />
            <Field palette={palette} label="Phone" value={org.phoneNumber} />
            <Field palette={palette} label="Website" value={org.websiteUrl} />
          </AppCard>

          <Text style={themed.section}>Address</Text>
          <AppCard style={styles.card}>
            <Field palette={palette} label="Address line 1" value={org.addressLine1} />
            <Field palette={palette} label="Address line 2" value={org.addressLine2} />
            <Field palette={palette} label="City" value={org.city} />
            <Field palette={palette} label="State" value={org.stateName} />
            <Field palette={palette} label="Country" value={org.countryCode} />
            <Field palette={palette} label="Postal code" value={org.postalCode} />
          </AppCard>

          <Text style={themed.section}>Regional settings</Text>
          <AppCard style={styles.card}>
            <Field palette={palette} label="Timezone" value={org.timeZone} />
            <Field palette={palette} label="Currency" value={org.defaultCurrencyCode} />
            <Field palette={palette} label="Date format" value={org.defaultDateFormat} />
            <Field palette={palette} label="Time format" value={org.defaultTimeFormat} />
          </AppCard>

          <View style={styles.actions}>
            {capabilities.canUpdateOrganization ? (
              <AppButton
                title="Edit"
                onPress={() => navigation.navigate(routes.organizationEdit)}
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md, marginBottom: spacing.sm },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg, marginBottom: spacing.xl },
});
